import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  FaBell,
  FaTimes,
  FaGraduationCap,
  FaHeartbeat,
  FaUsers,
  FaCalendarAlt,
  FaBook,
  FaClock,
  FaVideo,
  FaCommentAlt,
  FaHeart,
  FaRegComment,
  FaTicketAlt,
  FaAngleRight,
} from "react-icons/fa";
import { Drawer } from "@mui/material";
import { getNotifications } from "../../redux/actions/notifications";

const Container = styled.div`
  width: 100%;
  height: 100%;
  background: #f8f9fa;
  /* border-radius: 16px; */
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  background: linear-gradient(135deg, #ed1e24, #ee5a52);
  color: white;
  padding: 24px;
  position: relative;
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: 700;
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const NewUpdates = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  opacity: 0.9;
`;

const Dot = styled.span`
  width: 8px;
  height: 8px;
  background: #ffd93d;
  border-radius: 50%;
  animation: pulse 2s infinite;

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`;

const FilterTabs = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 16px;
  flex-wrap: wrap;
`;

const FilterTab = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }

  ${({ active }) =>
    active &&
    `
    background: rgba(255, 255, 255, 0.3);
  `}
`;

const Content = styled.div`
  padding: 16px;
  flex: 1;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const CategorySection = styled.div`
  margin-bottom: 24px;
`;

const CategoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding: 0 4px;
`;

const CategoryTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #2d3436;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CategorySubtitle = styled.span`
  font-size: 13px;
  color: #b2bec3;
  font-weight: 400;
`;

const Badge = styled.span`
  background: ${(props) => (props.unread ? "#ff6b6b" : "#dfe6e9")};
  color: ${(props) => (props.unread ? "white" : "#636e72")};
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
`;

const NotificationCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  border-left: 4px solid
    ${(props) => (props.unread ? "#74b9ff" : "transparent")};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: all 0.2s;
  cursor: pointer;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-1px);
  }

  ${({ type }) =>
    type === "wellness" &&
    `
    border-left-color: #fd79a8;
  `}

  ${({ type }) =>
    type === "community" &&
    `
    border-left-color: #a29bfe;
  `}

  ${({ type }) =>
    type === "events" &&
    `
    border-left-color: #00b894;
  `}
`;

const NotificationContent = styled.div`
  display: flex;
  gap: 12px;
`;

const IconWrapper = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${(props) => {
    switch (props.category) {
      case "academics":
        return "#e3f2fd";
      case "wellness":
        return "#fce4ec";
      case "community":
        return "#f3e5f5";
      case "events":
        return "#e8f5e9";
      default:
        return "#f5f5f5";
    }
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => {
    switch (props.category) {
      case "academics":
        return "#1976d2";
      case "wellness":
        return "#c2185b";
      case "community":
        return "#7b1fa2";
      case "events":
        return "#388e3c";
      default:
        return "#757575";
    }
  }};
  flex-shrink: 0;
`;

const TextContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const NotificationTitle = styled.h4`
  margin: 0 0 4px 0;
  font-size: 15px;
  font-weight: 600;
  color: #2d3436;
`;

const NotificationMessage = styled.p`
  margin: 0 0 8px 0;
  font-size: 13px;
  color: #636e72;
  line-height: 1.4;
`;

const Timestamp = styled.span`
  font-size: 12px;
  color: #b2bec3;
`;

const ActionButton = styled.button`
  background: ${(props) => (props.primary ? "#ff6b6b" : "transparent")};
  color: ${(props) => (props.primary ? "white" : "#ff6b6b")};
  border: ${(props) => (props.primary ? "none" : "1px solid #ff6b6b")};
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  margin-top: 8px;
  transition: all 0.2s;

  &:hover {
    background: ${(props) =>
      props.primary ? "#ee5a6f" : "rgba(255, 107, 107, 0.1)"};
  }
`;

const StatusDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${(props) => {
    if (props.type === "booking") return "#74b9ff";
    if (props.type === "wellness") return "#fd79a8";
    if (props.type === "community") return "#a29bfe";
    return "#00b894";
  }};
  margin-left: auto;
  flex-shrink: 0;
`;

const ViewAllButton = styled.button`
  width: calc(100% - 32px);
  margin: 0 16px 16px 16px;
  padding: 14px;
  background: linear-gradient(135deg, #ed1e24, #ee5a52);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: transform 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(255, 107, 107, 0.3);
  }
`;

const getCategoryColor = (type) => {
  switch (type) {
    case "BOOKING":
    case "RESCHEDULE":
    case "CANCELLATION":
    case "PAYMENT":
      return "academics";
    case "REVIEW":
      return "community";
    default:
      return "academics";
  }
};

const getIcon = (type) => {
  switch (type) {
    case "BOOKING":
      return <FaCalendarAlt />;
    case "RESCHEDULE":
      return <FaClock />;
    case "CANCELLATION":
      return <FaTimes />;
    case "PAYMENT":
      return <FaTicketAlt />;
    case "REVIEW":
      return <FaHeart />;
    default:
      return <FaBell />;
  }
};

const getTimeAgo = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));

  if (diffInHours < 1) {
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    return `${diffInMinutes} min ago`;
  } else if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours > 1 ? "s" : ""} ago`;
  } else {
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} day${diffInDays > 1 ? "s" : ""} ago`;
  }
};

const NotificationsPanel = ({ onClose }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { notifications: reduxNotifications, loading } = useSelector(
    (state) => state.notifications
  );
  const [activeFilter, setActiveFilter] = useState("all");

  useEffect(() => {
    dispatch(getNotifications());
  }, [dispatch]);

  const notificationsData = reduxNotifications;
  const unreadCount = notificationsData.filter((n) => !n.is_read).length;

  const groupedNotifications = {
    academics: notificationsData.filter((n) =>
      ["RESCHEDULE", "CANCELLATION", "PAYMENT"].includes(
        n.notification_type,
      ),
    ),
    wellness: notificationsData.filter((n) =>
      ["BOOKING",].includes(
        n.notification_type,
      ),
    ),
    community: notificationsData.filter((n) =>
      ["REVIEW"].includes(n.notification_type),
    ),
    events: [],
  };

  const getActionButton = (notification) => {
    switch (notification.notification_type) {
      case "BOOKING":
        return <ActionButton>View Details →</ActionButton>;
      case "RESCHEDULE":
        return <ActionButton primary>Confirm</ActionButton>;
      case "CANCELLATION":
        return <ActionButton>View Details →</ActionButton>;
      case "PAYMENT":
        return <ActionButton>View Receipt →</ActionButton>;
      case "REVIEW":
        return <ActionButton>Reply →</ActionButton>;
      default:
        return null;
    }
  };

  const renderNotification = (notification) => {
    const category = getCategoryColor(notification.notification_type);
    const icon = getIcon(notification.notification_type);

    return (
      <NotificationCard
        key={notification.id}
        unread={!notification.is_read}
        type={category}
      >
        <NotificationContent>
          <IconWrapper category={category}>{icon}</IconWrapper>
          <TextContent>
            <NotificationTitle>{notification.title}</NotificationTitle>
            <NotificationMessage>{notification.message}</NotificationMessage>
            <Timestamp>{getTimeAgo(notification.created_at)}</Timestamp>
            {/* {getActionButton(notification)} */}
          </TextContent>
          {!notification.is_read && <StatusDot type={category} />}
        </NotificationContent>
      </NotificationCard>
    );
  };

  return (
    <Container>
      <Header>
        <HeaderTop>
          <div>
            <Title>Notifications</Title>
            <NewUpdates>
              <Dot />
              {unreadCount} new updates
            </NewUpdates>
          </div>
          <CloseButton onClick={onClose}>
            <FaTimes />
          </CloseButton>
        </HeaderTop>
        <FilterTabs>
          <FilterTab
            active={activeFilter === "all"}
            onClick={() => setActiveFilter("all")}
          >
            <FaBell /> All
          </FilterTab>
          <FilterTab
            active={activeFilter === "classes"}
            onClick={() => setActiveFilter("classes")}
          >
            <FaGraduationCap /> Classes
          </FilterTab>
          <FilterTab
            active={activeFilter === "sessions"}
            onClick={() => setActiveFilter("sessions")}
          >
            <FaVideo /> Sessions
          </FilterTab>
          <FilterTab
            active={activeFilter === "feed"}
            onClick={() => setActiveFilter("feed")}
          >
            <FaUsers /> Feed
          </FilterTab>
        </FilterTabs>
      </Header>

      <Content>
        {groupedNotifications.academics.length > 0 && (
          <CategorySection>
            <CategoryHeader>
              <CategoryTitle>
                <FaBook style={{ color: "#1976d2" }} />
                Classes & Academics
                <CategorySubtitle>Recent updates</CategorySubtitle>
              </CategoryTitle>
              <Badge
                unread={groupedNotifications.academics.some((n) => !n.is_read)}
              >
                {
                  groupedNotifications.academics.filter((n) => !n.is_read)
                    .length
                }
              </Badge>
            </CategoryHeader>
            {groupedNotifications.academics.map(renderNotification)}
          </CategorySection>
        )}

        {groupedNotifications.wellness.length > 0 && (
          <CategorySection>
            <CategoryHeader>
              <CategoryTitle>
                {/* <FaHeartbeat style={{ color: "#c2185b" }} /> */}
                Wellness Sessions
                <CategorySubtitle>Upcoming sessions</CategorySubtitle>
              </CategoryTitle>
              <Badge
                unread={groupedNotifications.wellness.some((n) => !n.is_read)}
              >
                {groupedNotifications.wellness.filter((n) => !n.is_read).length}
              </Badge>
            </CategoryHeader>
            {groupedNotifications.wellness.map(renderNotification)}
          </CategorySection>
        )}

        {groupedNotifications.community.length > 0 && (
          <CategorySection>
            <CategoryHeader>
              <CategoryTitle>
                <FaUsers style={{ color: "#7b1fa2" }} />
                Community Feed
                <CategorySubtitle>Social updates</CategorySubtitle>
              </CategoryTitle>
              <Badge
                unread={groupedNotifications.community.some((n) => !n.is_read)}
              >
                {
                  groupedNotifications.community.filter((n) => !n.is_read)
                    .length
                }
              </Badge>
            </CategoryHeader>
            {groupedNotifications.community.map(renderNotification)}
          </CategorySection>
        )}

        {groupedNotifications.events.length > 0 && (
          <CategorySection>
            <CategoryHeader>
              <CategoryTitle>
                <FaCalendarAlt style={{ color: "#388e3c" }} />
                Campus Events
                <CategorySubtitle>Happening soon</CategorySubtitle>
              </CategoryTitle>
              <Badge
                unread={groupedNotifications.events.some((n) => !n.is_read)}
              >
                {groupedNotifications.events.filter((n) => !n.is_read).length}
              </Badge>
            </CategoryHeader>
            {groupedNotifications.events.map(renderNotification)}
          </CategorySection>
        )}
      </Content>

      <ViewAllButton onClick={() => { onClose(); history.push("/notifications"); }}>
        View All Notifications <FaAngleRight />
      </ViewAllButton>
    </Container>
  );
};




// Define static styles outside component
const staticStyles = {
  drawerPaper: {
    width: "30%",
    padding: 0,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  mainContainer: {
    mt: 2,
    display: "flex",
    height: "calc(100vh - 150px)",
  },
  sidebar: {
    border: "1px solid #E0E0E0",
    borderRadius: "4px",
    backgroundColor: "#f3f3f3",
    padding: "1rem",
    mr: 2,
    width: "25%",
  },
  contentArea: {
    flex: 1,
    pl: 2,
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 2,
    mt: 2,
  },
  formContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  nameRow: {
    display: "flex",
    gap: 2,
  },
  nameField: {
    flex: 1,
  },
  labelStyle: {
    mb: 1,
    fontWeight: "normal",
    fontSize: "12px",
  },
  titleStyle: {
    mb: "0.25rem",
    fontWeight: "bold",
    color: "#283A44",
    fontSize: "1.25rem",
  },
};


const NotificationsDrawer = ({open, onClose}) => {
  return <Drawer
    anchor="right"
    open={open}
    onClose={onClose}
    PaperProps={{
      sx: staticStyles.drawerPaper,
    }}
  >
    <NotificationsPanel onClose={onClose} />
  </Drawer>;
};

export default NotificationsDrawer;
